import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, AlertTriangle, TrendingUp } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, EmptyState } from "@/components/system";

type Investment = {
  id: string;
  display_name: string;
  institution: string | null;
  account_kind: string;
  currency: string;
  current_balance: number | null;
  balance_as_of: string | null;
  investment_provider: string | null;
  investment_tier: string | null;
  pfic_risk: boolean;
  region: string | null;
};

const TIER_DESC: Record<string, string> = {
  manual_snapshot: "Manual entry — paste balance periodically",
  csv_import: "Drop a CSV export from your broker",
  plaid_oauth: "Plaid OAuth (US/UK only)",
  direct_api: "Direct API (IBKR Client Portal, Schwab)",
};

export function FinanceInvestments() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const inv = useQuery({
    queryKey: ["finance_investments", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_account")
        .select("*")
        .eq("user_id", userId!)
        .in("account_kind", ["investment", "pension"])
        .eq("closed", false)
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as Investment[];
    },
  });

  const total = (inv.data ?? []).reduce((sum, a) => sum + Number(a.current_balance ?? 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)" }}>
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Investments</h1>
      </header>

      <article
        style={{
          padding: "var(--space-4)",
          borderRadius: "var(--radius-card)",
          border: "1px solid var(--hair)",
          background: "var(--bg-2)",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        <TrendingUp size={32} color="var(--accent)" />
        <div>
          <div className="mono" style={{ fontSize: 28, fontWeight: 500 }}>
            {formatMoney(total, "CHF")}
          </div>
          <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
            Total tracked across {inv.data?.length ?? 0} {inv.data?.length === 1 ? "account" : "accounts"}
          </span>
        </div>
      </article>

      <article
        style={{
          padding: "var(--space-3)",
          background: "var(--severity-warn-wash, #fdf6e3)",
          color: "var(--severity-warn, #b07c2a)",
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
        }}
      >
        <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
        <div>
          <strong>PFIC risk reminder.</strong> EU-domiciled ETFs in IBKR become Passive Foreign Investment Companies
          when you become a US tax resident — gains are taxed as ordinary income + interest charges, plus annual
          Form 8621. Liquidate before the J-1 treaty period ends or you trigger substantial-presence-test residency.
        </div>
      </article>

      {inv.isLoading ? (
        <SkeletonLoader variant="card" height={200} />
      ) : (inv.data ?? []).length === 0 ? (
        <EmptyState
          headline="No investment accounts yet."
          sub="Add IBKR, VIAC, Schwab, or pension accounts in /finance/accounts."
          cta={
            <Link to="/finance/accounts" style={{ color: "var(--accent)", fontSize: "var(--type-body-s)" }}>
              Add account →
            </Link>
          }
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(inv.data ?? []).map((a) => (
            <article
              key={a.id}
              style={{
                padding: "var(--space-4)",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-card)",
                background: "var(--bg-2)",
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <strong style={{ fontWeight: 500 }}>{a.display_name}</strong>
                <span style={{ fontSize: 12, color: "var(--fg-3)" }}>
                  {a.investment_provider ?? a.institution ?? "—"} · {a.region ?? "—"}
                </span>
                <span style={{ fontSize: 11, color: "var(--fg-3)" }}>
                  {TIER_DESC[a.investment_tier ?? "manual_snapshot"] ?? "Manual"}
                </span>
                {a.pfic_risk && (
                  <span style={{ fontSize: 11, color: "var(--severity-warn)" }}>⚠ PFIC risk flag</span>
                )}
              </div>
              <div className="mono" style={{ fontSize: 18, fontWeight: 500, textAlign: "right" }}>
                {formatMoney(a.current_balance, a.currency)}
                {a.balance_as_of && (
                  <div style={{ fontSize: 10, color: "var(--fg-3)", fontWeight: 400 }}>
                    as of {new Date(a.balance_as_of).toLocaleDateString()}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
